import {
    createStyles,
    FormHelperText,
    IconButton,
    InputLabel,
    makeStyles,
    Tooltip,
} from "@material-ui/core";
import classnames from "classnames";
import * as React from "react";

import { Chip, IChip, mergeClasses, RelodeTheme, Text } from "../";
import { PlusIcon, TrashIcon } from "../../assets/icons";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                backgroundColor: "transparent",
                marginBottom: theme.spacing(2),
            },
            description: {
                fontSize: "14px",
            },
            tagList: {
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                alignItems: "center",
                marginTop: theme.spacing(1),
                "& div:last-child": {
                    marginRight: 0,
                },
                "& div": {
                    marginRight: 8,
                    marginBottom: 8,
                },
            },
            categorizedTagList: {
                marginTop: theme.spacing(1),
                "& div:last-child": {
                    marginRight: 0,
                },
                "& div": {
                    marginRight: 8,
                    marginBottom: 8,
                },
            },
            addButton: {
                display: "inline-flex",
                width: theme.spacing(4),
                height: theme.spacing(4),
                alignItems: "center",
                justifyContent: "center",
                borderRadius: theme.spacing(2),
                whiteSpace: "nowrap",
                padding: 0,
                marginBottom: theme.spacing(1),
                verticalAlign: "middle",
                fontSize: "1rem",
                backgroundColor: theme.relode.colors.chip.matched.light,
                color: theme.relode.colors.chip.matched.contrastText,

                "&:focus, &:active": {
                    backgroundColor: theme.relode.colors.chip.matched.light,
                },
                "&:hover": {
                    backgroundColor: theme.relode.colors.chip.matched.main,
                    boxShadow: "none",
                },
            },
            error: {
                color: theme.palette.error.main,
            },
            helperText: {
                marginBottom: theme.spacing(3),
            },
            categoryText: {
                fontSize: "14px",
                color: "rgba(0, 0, 0, 0.54)",
            },
            inputLabelContainer: {
                display: "flex",
                alignItems: "center",
            },
            selectAllButton: {
                marginLeft: theme.spacing(1),
            },
            chipCategoryLabelContainer: {
                display: "flex",
                alignItems: "center",
            },
        }),
    { name: "ChipCollection" }
);

interface OwnProps {
    classes?: Partial<ReturnType<typeof useStyles>>;
    id?: string;
    description?: string;
    chips: IChip[] | IChip;
    showGroupName?: boolean;
    categorize?: boolean;
    style?: React.CSSProperties;
    groupSelect?: boolean;
    dataCy?: string;
    onGroupDelete?: (chipIds: number[]) => void;
    onGroupSelect?: (chipIds: number[], groupId: number) => void;
    onChipClick?: (chipId: number, value?: IChip) => void;
    onChipDelete?: (chipId: number, chip: IChip) => void;
    onAdd?: () => void; // more chips can be added, shows + button
    onRemoveAll?: () => void;
    error?: boolean;
    helperText?: string;
}

interface Props extends OwnProps {}

export interface ChipCollectionProps extends OwnProps {}

const groupBy = (list: IChip[], keyGetter: (chip: IChip) => string) => {
    const map = new Map();
    list.forEach(item => {
        const key = keyGetter(item);
        const collection = map.get(key);
        if (!collection) {
            map.set(key, [item]);
        } else {
            collection.push(item);
        }
    });
    return map;
};

export const ChipCollection = (props: Props) => {
    const classes = mergeClasses(useStyles({}), props.classes);

    const {
        chips,
        showGroupName,
        style,
        onChipDelete,
        onChipClick,
        categorize,
        onGroupDelete,
        description,
        groupSelect,
        error,
        helperText,
        id,
        onGroupSelect,
        dataCy
    } = props;

    const renderChip = (chip: IChip) => (
        <Chip
            key={`${chip.id}-${chip.name}`}
            style={style}
            value={chip}
            label={`${
                showGroupName && chip.groupName ? chip.groupName + ":" : ""
            } ${chip.name}`}
            id={chip.id}
            selected={chip.isMatched}
            enabled={chip.canEdit}
            onChipClick={onChipClick && chip.canEdit ? onChipClick : undefined}
            onChipDelete={
                onChipDelete && chip.canEdit ? onChipDelete : undefined
            }
            dataCy={dataCy + `${chip.id}-chip`} //still trying to pass this prop into component through parent component
        />
    );

    const renderChips = () => {
        if (!chips || !Array.isArray(chips) || chips.length === 0) return null;

        const chipsArray = Array.isArray(chips) ? chips : [chips];

        const listOfCategories = Array.from(
            new Set(chipsArray.map((chip: IChip) => `${chip.groupName}`))
        );

        const groupedChips = groupBy(
            chipsArray.sort((a, b) => a.name.localeCompare(b.name)),
            (chip: IChip) => chip.groupName
        );

        const chipsGroupedByCategory = listOfCategories
            .map(category => ({
                categoryName: category,
                categoryChips: groupedChips.get(category),
            }))
            .sort((a, b) => a.categoryName.localeCompare(b.categoryName));

        const removeChipGroup = (chips: IChip[]) => {
            const chipIds = chips.map(x => x.id);
            onGroupDelete(chipIds);
        };

        const lowerCaseChipGroupName = (groupName: string) =>
            groupName === "Healthcare IT"
                ? groupName.charAt(0).toLowerCase() + groupName.slice(1)
                : groupName === "IT"
                ? groupName
                : groupName.toLocaleLowerCase();

        return (
            <>
                {categorize
                    ? chipsGroupedByCategory.map(group => (
                          <div>
                              <div
                                  className={classes.chipCategoryLabelContainer}
                              >
                                  <Text
                                      variant="body1"
                                      className={classes.categoryText}
                                  >
                                      {lowerCaseChipGroupName(
                                          group.categoryName
                                      )}
                                  </Text>
                                  {onGroupDelete ? (
                                      <Tooltip
                                          placement="bottom-start"
                                          key={group.categoryName}
                                          title={`remove all ${group.categoryName}`}
                                      >
                                          <IconButton
                                              color="primary"
                                              size="small"
                                              className={
                                                  classes.selectAllButton
                                              }
                                              onClick={() =>
                                                  removeChipGroup(
                                                      group.categoryChips
                                                  )
                                              }
                                          >
                                              <TrashIcon
                                                  color="primary"
                                                  fontSize="small"
                                              />
                                          </IconButton>
                                      </Tooltip>
                                  ) : null}
                              </div>

                              <div>
                                  {group.categoryChips.map(chip =>
                                      renderChip(chip)
                                  )}
                              </div>
                          </div>
                      ))
                    : chipsArray.map(chip => renderChip(chip))}
            </>
        );
    };

    const hasChips = () => {
        const { chips } = props;
        if (!chips) return false;

        if (Array.isArray(chips)) return chips.length > 0;

        return true;
    };

    const labelClasses = classnames(classes.description, {
        [classes.error]: error,
    });
    const helperTextClasses = classnames(classes.helperText, {
        [classes.error]: error,
    });

    const selectAll = () => {
        if (chips && Array.isArray(chips) && chips.length > 0) {
            const chipIds = chips.map(chip => chip.id);
            onGroupSelect(chipIds, chips[0].groupId);
        }
    };

    return (
        <div className={classes.root} id={`${id}ChipCollectionWrapper`}>
            {hasChips() && description ? (
                <div className={classes.inputLabelContainer}>
                    <InputLabel className={labelClasses}>
                        {description}
                    </InputLabel>
                    {groupSelect ? (
                        <Tooltip
                            placement="bottom-start"
                            key={description}
                            title={`select all ${description}`}
                        >
                            <IconButton
                                className={classes.selectAllButton}
                                color="primary"
                                size="small"
                                onClick={selectAll}
                            >
                                <PlusIcon color="primary" fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    ) : null}
                </div>
            ) : null}
            {error ? (
                <FormHelperText className={helperTextClasses}>
                    {helperText}
                </FormHelperText>
            ) : null}
            {hasChips() ? (
                <div
                    className={
                        categorize
                            ? classes.categorizedTagList
                            : classes.tagList
                    }
                    id={`${id}ChipCollectionTagList`}
                >
                    {renderChips()}
                </div>
            ) : null}
        </div>
    );
};
